/**
 * Build-time configuration for the frontend.
 * API endpoints are fixed at build time via NEXT_PUBLIC_* env vars.
 */

import { AppConfig, BackendConfigResponse } from '@/lib/types/config'
import { desktopCall, isDesktopBridgeAvailable, startDesktopRuntime } from '@/lib/desktop/bridge'
import { runtimeTarget } from '@/lib/runtime-target'
import { fetchWithAuth } from '@/lib/utils/request'

// Build timestamp for debugging - set at build time
const BUILD_TIME = new Date().toISOString()

let config: AppConfig | null = null
let configPromise: Promise<AppConfig> | null = null

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms))
}

async function waitForDesktopBridge(timeoutMs = 4000): Promise<boolean> {
  if (runtimeTarget !== 'desktop') {
    return false
  }

  if (isDesktopBridgeAvailable()) {
    return true
  }

  if (typeof window === 'undefined') {
    return false
  }

  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    await sleep(100)
    if (isDesktopBridgeAvailable()) {
      return true
    }
  }

  return false
}

/**
 * Get the API URL to use for requests.
 *
 * Priority:
 * 1. Environment variable (NEXT_PUBLIC_API_URL)
 * 2. Default fallback (http://localhost:5055)
 */
export async function getApiUrl(): Promise<string> {
  // If we already have config, return it
  if (config) {
    return config.apiUrl
  }

  // If we're already fetching, wait for that
  if (configPromise) {
    const cfg = await configPromise
    return cfg.apiUrl
  }

  // Start fetching config
  configPromise = fetchConfig()
  const cfg = await configPromise
  return cfg.apiUrl
}

/**
 * Get the Matrix API URL to use for requests.
 */
export async function getMatrixApiUrl(): Promise<string | undefined> {
  // If we already have config, return it
  if (config) {
    return config.matrixApiUrl
  }

  // If we're already fetching, wait for that
  if (configPromise) {
    const cfg = await configPromise
    return cfg.matrixApiUrl
  }

  // Start fetching config
  configPromise = fetchConfig()
  const cfg = await configPromise
  return cfg.matrixApiUrl
}

/**
 * Get the Cuss API URL to use for requests.
 */
export async function getCussApiUrl(): Promise<string | undefined> {
  // If we already have config, return it
  if (config) {
    return config.cussApiUrl
  }

  // If we're already fetching, wait for that
  if (configPromise) {
    const cfg = await configPromise
    return cfg.cussApiUrl
  }

  // Start fetching config
  configPromise = fetchConfig()
  const cfg = await configPromise
  return cfg.cussApiUrl
}

/**
 * Get the GG API URL to use for requests.
 */
export async function getGgApiUrl(): Promise<string | undefined> {
  // If we already have config, return it
  if (config) {
    return config.ggApiUrl
  }

  // If we're already fetching, wait for that
  if (configPromise) {
    const cfg = await configPromise
    return cfg.ggApiUrl
  }

  // Start fetching config
  configPromise = fetchConfig()
  const cfg = await configPromise
  return cfg.ggApiUrl
}

/**
 * Get the Athena API URL to use for requests.
 */
export async function getAthenaApiUrl(): Promise<string | undefined> {
  // If we already have config, return it
  if (config) {
    return config.athenaApiUrl
  }

  // If we're already fetching, wait for that
  if (configPromise) {
    const cfg = await configPromise
    return cfg.athenaApiUrl
  }

  // Start fetching config
  configPromise = fetchConfig()
  const cfg = await configPromise
  return cfg.athenaApiUrl
}

/**
 * Get the Auth API URL to use for auth requests.
 *
 * All auth-related API calls go through the remote auth service,
 * separated from the main API URL.
 *
 * Priority:
 * 1. Environment variable (NEXT_PUBLIC_AUTH_API_URL)
 * 2. Default fallback (https://agent.fkxiaoyu.com)
 */
export async function getAuthApiUrl(): Promise<string> {
  // If we already have config, return it
  if (config) {
    return config.authApiUrl || 'https://agent.fkxiaoyu.com'
  }

  // If we're already fetching, wait for that
  if (configPromise) {
    const cfg = await configPromise
    return cfg.authApiUrl || 'https://agent.fkxiaoyu.com'
  }

  // Start fetching config
  configPromise = fetchConfig()
  const cfg = await configPromise
  return cfg.authApiUrl || 'https://agent.fkxiaoyu.com'
}

/**
 * Get the Dashboard API URL to use for requests.
 */
export async function getDashboardApiUrl(): Promise<string> {
  // If we already have config, return it
  if (config) {
    return config.dashboardApiUrl || config.apiUrl
  }

  // If we're already fetching, wait for that
  if (configPromise) {
    const cfg = await configPromise
    return cfg.dashboardApiUrl || cfg.apiUrl
  }

  // Start fetching config
  configPromise = fetchConfig()
  const cfg = await configPromise
  return cfg.dashboardApiUrl || cfg.apiUrl
}

/**
 * Get the full configuration.
 */
export async function getConfig(): Promise<AppConfig> {
  if (config) {
    return config
  }

  if (configPromise) {
    return await configPromise
  }

  configPromise = fetchConfig()
  return await configPromise
}

/**
 * Fetch configuration from the API or use defaults.
 */
async function fetchConfig(): Promise<AppConfig> {
  console.log('🔧 [Config] Starting configuration detection...')
  console.log('🔧 [Config] Build time:', BUILD_TIME)

  // STEP 1: Build-time environment variables
  const envApiUrl = process.env.NEXT_PUBLIC_API_URL
  const envDesktopApiUrl = process.env.NEXT_PUBLIC_DESKTOP_API_URL
  const envMatrixApiUrl = process.env.NEXT_PUBLIC_MATRIX_API_URL
  const envCussApiUrl = process.env.NEXT_PUBLIC_CUSS_API_URL
  const envGgApiUrl = process.env.NEXT_PUBLIC_GG_API_URL
  const envAthenaApiUrl = process.env.NEXT_PUBLIC_ATHENA_API_URL
  const envDashboardApiUrl = process.env.NEXT_PUBLIC_DASHBOARD_API_URL
  const envAuthApiUrl = process.env.NEXT_PUBLIC_AUTH_API_URL
  console.log('🔧 [Config] NEXT_PUBLIC_API_URL from build:', envApiUrl || '(not set)')
  console.log('🔧 [Config] NEXT_PUBLIC_DESKTOP_API_URL from build:', envDesktopApiUrl || '(not set)')
  console.log('🔧 [Config] NEXT_PUBLIC_MATRIX_API_URL from build:', envMatrixApiUrl || '(not set)')
  console.log('🔧 [Config] NEXT_PUBLIC_CUSS_API_URL from build:', envCussApiUrl || '(not set)')
  console.log('🔧 [Config] NEXT_PUBLIC_GG_API_URL from build:', envGgApiUrl || '(not set)')
  console.log('🔧 [Config] NEXT_PUBLIC_ATHENA_API_URL from build:', envAthenaApiUrl || '(not set)')
  console.log('🔧 [Config] NEXT_PUBLIC_DASHBOARD_API_URL from build:', envDashboardApiUrl || '(not set)')
  console.log('🔧 [Config] NEXT_PUBLIC_AUTH_API_URL from build:', envAuthApiUrl || '(not set)')

  // STEP 1.5: desktop native bridge (preferred in desktop runtime)
  // Falls back to HTTP only when the bridge is unavailable.
  const desktopBridgeAvailable = await waitForDesktopBridge()
  if (desktopBridgeAvailable) {
    try {
      const runtimeStatus = await startDesktopRuntime(true)
      const desktopCfg = await desktopCall<{
        deploy_mode: string
        api_url?: string
        data_folder?: string
      }>('system.config')
      const desktopHealth = await desktopCall<{
        status: string
      }>('system.health')
      const runtimeApiUrl = runtimeStatus.ports?.api
        ? `http://127.0.0.1:${runtimeStatus.ports.api}`
        : undefined
      const apiUrl = runtimeApiUrl || desktopCfg.api_url || envDesktopApiUrl || envApiUrl || 'http://127.0.0.1:5055'

      console.log('🔧 [Config] Desktop runtime status:', {
        state: runtimeStatus.state,
        apiPort: runtimeStatus.ports?.api,
        postgresHealthy: runtimeStatus.services?.postgres?.healthy,
        apiHealthy: runtimeStatus.services?.api?.healthy,
      })

      console.log('🔧 [Config] Verifying desktop API config from:', `${apiUrl}/agent-api/config`)
      const apiConfigResponse = await fetchWithAuth(`${apiUrl}/agent-api/config`, {
        cache: 'no-store',
      })
      if (!apiConfigResponse.ok) {
        throw new Error(`Desktop API config endpoint returned status ${apiConfigResponse.status}`)
      }
      const apiConfig: BackendConfigResponse = await apiConfigResponse.json()

      config = {
        apiUrl,
        matrixApiUrl: envMatrixApiUrl || undefined,
        cussApiUrl: envCussApiUrl || undefined,
        ggApiUrl: envGgApiUrl || undefined,
        athenaApiUrl: envAthenaApiUrl || undefined,
        dashboardApiUrl: envDashboardApiUrl || undefined,
        authApiUrl: envAuthApiUrl || undefined,
        version: apiConfig.version || 'desktop-local',
        buildTime: BUILD_TIME,
        latestVersion: apiConfig.latestVersion || null,
        hasUpdate: apiConfig.hasUpdate || false,
        dbStatus: apiConfig.dbStatus,
        deployMode: apiConfig.deployMode || (desktopCfg.deploy_mode as 'desktop' | 'server') || 'desktop',
      }
      console.log('✅ [Config] Loaded configuration from desktop bridge:', {
        deployMode: config.deployMode,
        bridgeHealth: desktopHealth.status,
        apiUrl: config.apiUrl,
        dbStatus: config.dbStatus,
      })
      return config
    } catch (desktopBridgeError) {
      console.warn('⚠️ [Config] Desktop bridge call failed:', desktopBridgeError)
      throw desktopBridgeError
    }
  }

  // STEP 2: Runtime defaults
  // desktop runtime can explicitly set NEXT_PUBLIC_DESKTOP_API_URL
  let defaultApiUrl = runtimeTarget === 'desktop'
    ? (envDesktopApiUrl || 'http://127.0.0.1:5055')
    : 'http://localhost:5055'

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    const protocol = window.location.protocol
    console.log('🔧 [Config] Current frontend URL:', `${protocol}//${hostname}${window.location.port ? ':' + window.location.port : ''}`)

    // If not localhost, use the same hostname with port 5055
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      defaultApiUrl = `${protocol}//${hostname}:5055`
      console.log('🔧 [Config] Detected remote hostname, using:', defaultApiUrl)
    } else {
      console.log('🔧 [Config] Detected localhost, using:', defaultApiUrl)
    }
  }

  // Priority:
  // web: NEXT_PUBLIC_API_URL > smart default
  // desktop: NEXT_PUBLIC_DESKTOP_API_URL > NEXT_PUBLIC_API_URL > desktop default
  const baseUrl = runtimeTarget === 'desktop'
    ? (envDesktopApiUrl || envApiUrl || defaultApiUrl)
    : (envApiUrl || defaultApiUrl)
  console.log('🔧 [Config] Final base URL to try:', baseUrl)
  console.log('🔧 [Config] Selection priority: build-time=' + (envApiUrl ? '✅' : '❌') +
    ', smart-default=' + (!envApiUrl ? '✅' : '❌'))

  try {
    console.log('🔧 [Config] Fetching backend config from:', `${baseUrl}/agent-api/config`)
    // Try to fetch runtime config from backend API
    const response = await fetchWithAuth(`${baseUrl}/agent-api/config`, {
      cache: 'no-store',
    })

    if (response.ok) {
      const data: BackendConfigResponse = await response.json()
      config = {
        apiUrl: baseUrl, // Use baseUrl from runtime-config (Python no longer returns this)
        matrixApiUrl: envMatrixApiUrl || undefined,
        cussApiUrl: envCussApiUrl || undefined,
        ggApiUrl: envGgApiUrl || undefined,
        athenaApiUrl: envAthenaApiUrl || undefined,
        dashboardApiUrl: envDashboardApiUrl || undefined,
        authApiUrl: envAuthApiUrl || undefined,
        version: data.version || 'unknown',
        buildTime: BUILD_TIME,
        latestVersion: data.latestVersion || null,
        hasUpdate: data.hasUpdate || false,
        dbStatus: data.dbStatus, // Can be undefined for old backends
        deployMode: data.deployMode,
      }
      console.log('✅ [Config] Successfully loaded API config:', config)
      return config
    } else {
      // Don't log error here - ConnectionGuard will display it
      throw new Error(`API config endpoint returned status ${response.status}`)
    }
  } catch (error) {
    // Don't log error here - ConnectionGuard will display it with proper UI
    throw error
  }
}

/**
 * Reset the configuration cache (useful for testing).
 */
export function resetConfig(): void {
  config = null
  configPromise = null
}
